import { useRef, useState, type ChangeEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/ApiProvider.js';
import { parseBirthdayFile, type ParsedBirthday, type ParseError } from './birthdayParse.js';
import styles from './BirthdayImport.module.css';

export interface BirthdayImportProps {
  classroomId: string;
}

/**
 * Bulk import birthdays from a two-column spreadsheet (CSV or
 * XLSX/XLS). Column 1 = student name, column 2 = birthday.
 *
 * Flow: pick file → parse → show a single-line summary
 * ("Found 12 birthdays in roster.xlsx") + Add/Cancel buttons →
 * "Add all" loops through the existing single-add API. We
 * deliberately do NOT preview the per-row list — Sarah just wants
 * a confirmation, not a roster dump. Skipped rows still show their
 * compact "row → reason" notes so the teacher knows what to fix.
 */
export function BirthdayImport({ classroomId }: BirthdayImportProps) {
  const api = useApi();
  const queryClient = useQueryClient();
  const fileRef = useRef<globalThis.HTMLInputElement | null>(null);

  const [parsed, setParsed] = useState<ParsedBirthday[]>([]);
  const [errors, setErrors] = useState<ParseError[]>([]);
  const [parseStatus, setParseStatus] = useState<'idle' | 'parsing' | 'error'>('idle');
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [addProgress, setAddProgress] = useState<{ done: number; total: number } | null>(null);
  const [loadedSummary, setLoadedSummary] = useState<string | null>(null);

  const addAll = useMutation({
    mutationFn: async (rows: ParsedBirthday[]) => {
      setAddProgress({ done: 0, total: rows.length });
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i]!;
        await api.calendar.createBirthday(classroomId, {
          name: r.name,
          month: r.month,
          day: r.day,
        });
        setAddProgress({ done: i + 1, total: rows.length });
      }
      return rows.length;
    },
    onSuccess: (count) => {
      void queryClient.invalidateQueries({ queryKey: ['calendar-birthdays', classroomId] });
      // Replace the preview with a single confirmation line; it
      // auto-clears after a few seconds so the panel doesn't grow.
      setLoadedSummary(`Loaded ${count} birthday${count === 1 ? '' : 's'} ✓`);
      setParsed([]);
      setErrors([]);
      setFileName(null);
      setAddProgress(null);
      setTimeout(() => setLoadedSummary(null), 4000);
    },
  });

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Allow re-picking the same file later by clearing the input value.
    e.target.value = '';
    if (!file) return;

    setParseStatus('parsing');
    setParseError(null);
    setParsed([]);
    setErrors([]);
    setAddProgress(null);
    setLoadedSummary(null);
    setFileName(file.name);

    try {
      const result = await parseBirthdayFile(file);
      setParsed(result.birthdays);
      setErrors(result.errors);
      setParseStatus(result.birthdays.length > 0 ? 'idle' : 'error');
      if (result.birthdays.length === 0 && result.errors.length === 0) {
        setParseError(
          'No rows found. The file should have one student per row with name in column 1 and birthday in column 2.',
        );
      }
    } catch (err) {
      setParseStatus('error');
      setParseError(
        err instanceof Error ? `Couldn’t read the file: ${err.message}` : 'Couldn’t read the file.',
      );
    }
  };

  const cancel = () => {
    setParsed([]);
    setErrors([]);
    setParseError(null);
    setAddProgress(null);
    setParseStatus('idle');
    setFileName(null);
  };

  const hasPreview = parsed.length > 0 || errors.length > 0;
  const adding = addAll.isPending || addProgress !== null;

  return (
    <div>
      <div className={styles.row}>
        <button
          type="button"
          className={styles.fileButton}
          onClick={() => fileRef.current?.click()}
          disabled={parseStatus === 'parsing' || adding}
          data-testid="birthday-import-button"
        >
          {parseStatus === 'parsing' ? 'Reading…' : 'Import from spreadsheet…'}
        </button>
        <span className={styles.hint}>
          .csv, .xlsx, or .xls — 2 columns: <strong>Name</strong> in col 1,{' '}
          <strong>Birthday</strong> in col 2.
        </span>
      </div>

      <input
        ref={fileRef}
        type="file"
        className={styles.fileInput}
        accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={onFile}
        data-testid="birthday-import-file"
      />

      {parseError ? <p className={`${styles.status} ${styles.statusError}`}>{parseError}</p> : null}

      {loadedSummary ? (
        <p
          className={`${styles.status} ${styles.statusSuccess}`}
          data-testid="birthday-import-loaded"
        >
          {loadedSummary}
        </p>
      ) : null}

      {hasPreview ? (
        <div className={styles.preview} data-testid="birthday-import-preview">
          <div className={styles.previewHead}>
            <span className={styles.previewSummary}>
              {parsed.length > 0
                ? `Found ${parsed.length} birthday${parsed.length === 1 ? '' : 's'}`
                : 'No valid rows'}
              {fileName ? ` in ${fileName}` : ''}
              {errors.length > 0 ? ` · ${errors.length} skipped` : ''}
            </span>
            <div className={styles.previewActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={cancel}
                disabled={adding}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.addAllButton}
                onClick={() => addAll.mutate(parsed)}
                disabled={parsed.length === 0 || adding}
                data-testid="birthday-import-add-all"
              >
                {adding
                  ? `Loading ${addProgress?.done ?? 0} of ${addProgress?.total ?? parsed.length}…`
                  : `Load ${parsed.length} birthday${parsed.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>

          {/* Per-row preview list intentionally omitted — Sarah only
              wants a confirmation, not a roster dump. Skipped rows
              still show below so the teacher can fix bad data. */}

          {errors.length > 0 ? (
            <div className={styles.errors}>
              <span className={styles.errorsHead}>Skipped rows</span>
              {errors.slice(0, 10).map((err, i) => (
                <span key={i} className={styles.errorItem}>
                  <span className={styles.errorRow}>#{err.row}</span>
                  {err.reason} — &ldquo;{err.raw}&rdquo;
                </span>
              ))}
              {errors.length > 10 ? (
                <span className={styles.errorItem}>…and {errors.length - 10} more</span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
